import { useEffect, useState } from 'react';
import { Button, Card, CardContent, DialogActions, DialogContent, MenuItem, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { Dialog, DialogTitle } from '../Common/ModalDialog';
import { API_ADD_FINANCE_RECORD, API_UPDATE_FINANCE_RECORD } from '../Config/ApiUrlConstant';
import { useAuth } from '../AuthContext';

const receivedCategories = ['Tuition Fees', 'Admission Fees', 'Donations', 'Other Income'];
const spentCategories = ['Salary', 'Rent', 'Utilities', 'Stationery', 'Transport', 'Maintenance', 'Other Expense'];
const emptyForm = { entry_date: '', amount: '', type: 'RECEIVED', category: 'Tuition Fees', payment_mode: 'CASH', status: 'CONFIRMED', person_name: '', note: '' };
const selectProps = { MenuProps: { PaperProps: { className: 'finance-select-menu' } } };

export default function AddEditFinanceForm({ initialData, onSaved, onCancel }) {
  const { logout } = useAuth();
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const isEditing = Boolean(initialData?.id);
  const categories = Array.from(new Set([...(formData.type === 'SPENT' ? spentCategories : receivedCategories), formData.category]));

  useEffect(() => {
    if (!initialData) {
      setFormData(emptyForm);
      return;
    }
    setFormData({
      id: initialData.id,
      voucher_no: initialData.voucher_no || '',
      entry_date: String(initialData.entry_date || '').slice(0, 10),
      amount: initialData.amount || '',
      type: initialData.type || 'RECEIVED',
      category: initialData.category || 'Other Income',
      payment_mode: initialData.payment_mode || 'CASH',
      status: initialData.status || 'CONFIRMED',
      person_name: initialData.person_name || '',
      note: initialData.note || ''
    });
  }, [initialData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => {
      if (name === 'type') {
        return { ...current, type: value, category: value === 'SPENT' ? spentCategories[0] : receivedCategories[0] };
      }
      return { ...current, [name]: value };
    });
  };

  const saveRecord = async () => {
    setConfirmationOpen(false);
    setSubmitting(true);
    try {
      const response = await fetch(isEditing ? API_UPDATE_FINANCE_RECORD : API_ADD_FINANCE_RECORD, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(formData)
      });
      const payload = await response.json();
      if (response.status === 401 || response.status === 403) {
        logout();
        return;
      }
      if (!response.ok || !payload.status) {
        setFeedback({ title: 'Unable to save record', message: payload.message || 'Please review the entry and try again.' });
        return;
      }
      setFeedback({ title: isEditing ? 'Record updated' : 'Record saved', message: isEditing ? 'The finance record has been updated successfully.' : `Voucher ${payload.voucher_no} was generated successfully.` });
      setFormData(emptyForm);
      onSaved?.();
    } catch (error) {
      console.error('Error saving finance record:', error);
      setFeedback({ title: 'Unable to save record', message: 'Please check your connection and try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setConfirmationOpen(true);
  };

  return <><Card className="finance-card finance-entry-card" sx={{ maxWidth: 1000, mx: 'auto', mt: 3 }}><CardContent className="finance-card-content" component="form" onSubmit={handleSubmit}>
    <div className="finance-card-heading"><div><Typography variant="h6">{isEditing ? 'Edit Finance Record' : 'Add Received / Spent Record'}</Typography><Typography variant="body2" color="text.secondary">{isEditing ? 'Voucher type is locked after creation to protect the accounting trail.' : 'Choose where the money came from or where it was spent.'}</Typography></div>{isEditing ? <span className="finance-edit-pill">Editing</span> : null}</div>
    {!isEditing ? <p className="finance-voucher-hint">Voucher number is generated automatically when the record is saved.</p> : null}
    <Grid container spacing={2}>
      {isEditing ? <Grid item xs={12} sm={6} md={3}><TextField fullWidth disabled label="Voucher Number" value={formData.voucher_no} /></Grid> : null}
      <Grid item xs={12} sm={6} md={3}><TextField fullWidth required type="date" label="Date" name="entry_date" value={formData.entry_date} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>
      <Grid item xs={12} sm={6} md={3}><TextField fullWidth required label="Amount" name="amount" type="number" inputProps={{ min: '0.01', step: '0.01' }} value={formData.amount} onChange={handleChange} /></Grid>
      <Grid item xs={12} sm={6} md={3}><TextField fullWidth required select disabled={isEditing} label="Entry Type" name="type" value={formData.type} onChange={handleChange} InputLabelProps={{ shrink: true }} SelectProps={selectProps}><MenuItem value="RECEIVED">Received</MenuItem><MenuItem value="SPENT">Spent</MenuItem></TextField></Grid>
      <Grid item xs={12} sm={6} md={3}><TextField fullWidth required select label="Category" name="category" value={formData.category} onChange={handleChange} InputLabelProps={{ shrink: true }} SelectProps={selectProps}>{categories.map((category) => <MenuItem key={category} value={category}>{category}</MenuItem>)}</TextField></Grid>
      <Grid item xs={12} sm={6} md={3}><TextField fullWidth required select label="Payment Mode" name="payment_mode" value={formData.payment_mode} onChange={handleChange} InputLabelProps={{ shrink: true }} SelectProps={selectProps}><MenuItem value="CASH">Cash</MenuItem><MenuItem value="BANK">Bank</MenuItem><MenuItem value="UPI">UPI</MenuItem><MenuItem value="CHEQUE">Cheque</MenuItem><MenuItem value="OTHER">Other</MenuItem></TextField></Grid>
      <Grid item xs={12} sm={6} md={3}><TextField fullWidth required select label="Record Status" name="status" value={formData.status} onChange={handleChange} InputLabelProps={{ shrink: true }} SelectProps={selectProps}><MenuItem value="DRAFT">Draft</MenuItem><MenuItem value="CONFIRMED">Confirmed</MenuItem><MenuItem value="CANCELLED">Cancelled</MenuItem></TextField></Grid>
      <Grid item xs={12} sm={6}><TextField fullWidth required label={formData.type === 'SPENT' ? 'Paid To / Vendor' : 'Received From'} name="person_name" value={formData.person_name} onChange={handleChange} /></Grid>
      <Grid item xs={12} sm={6}><TextField fullWidth label="Narration / Note" name="note" value={formData.note} onChange={handleChange} /></Grid>
      <Grid item xs={12} className="finance-form-actions"><Button type="submit" variant="contained" size="large" disabled={submitting}>{submitting ? 'Saving...' : isEditing ? 'Update Record' : 'Save Record'}</Button>{isEditing ? <Button type="button" variant="outlined" size="large" onClick={onCancel}>Cancel Edit</Button> : null}</Grid>
    </Grid>
  </CardContent></Card>
  <Dialog open={confirmationOpen} onClose={() => !submitting && setConfirmationOpen(false)} maxWidth="xs" fullWidth>
    <DialogTitle>{isEditing ? 'Update finance record?' : 'Save finance record?'}</DialogTitle>
    <DialogContent><Typography>Are you sure you want to {isEditing ? 'update' : 'save'} this {formData.type.toLowerCase()} entry for Rs. {formData.amount || '0'}?</Typography></DialogContent>
    <DialogActions><Button onClick={() => setConfirmationOpen(false)} disabled={submitting}>Cancel</Button><Button variant="contained" onClick={saveRecord} disabled={submitting}>{submitting ? 'Saving...' : 'Confirm'}</Button></DialogActions>
  </Dialog>
  <Dialog open={Boolean(feedback)} onClose={() => setFeedback(null)} maxWidth="xs" fullWidth>
    <DialogTitle>{feedback?.title}</DialogTitle>
    <DialogContent><Typography>{feedback?.message}</Typography></DialogContent>
    <DialogActions><Button variant="contained" onClick={() => setFeedback(null)}>Close</Button></DialogActions>
  </Dialog></>;
}
